// Exercise Category Service
// This service handles the exercise category tree data and provides utilities to work with it

import { fetchExerciseCategoryTabTree } from './exercisePlanAPI.js'

// Cache for the exercise category data
let cachedCategoryData = null
let cacheTimestamp = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Load exercise category data from API with caching
 * @returns {Promise<Object>} Exercise category data
 */
export const loadExerciseCategoryData = async () => {
  try {
    // Check if we have valid cached data
    if (cachedCategoryData && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      console.log('📋 Using cached exercise category data')
      return cachedCategoryData
    }

    console.log('🔄 Loading exercise category data from API...')
    const data = await fetchExerciseCategoryTabTree()
    
    // Cache the data
    cachedCategoryData = data
    cacheTimestamp = Date.now()
    
    return data
  } catch (error) {
    console.error('❌ Failed to load exercise category data from API:', error)
    
    // If we have cached data, use it as fallback
    if (cachedCategoryData) {
      console.warn('⚠️ Using cached data as fallback')
      return cachedCategoryData
    }
    
    // If no cached data and API fails, return empty structure
    console.warn('⚠️ No cached data available, returning empty structure')
    return { exerciseplancategoryindex: '[]' }
  }
}

/**
 * Parse the exercise category tree from the data
 * @param {Object} categoryData - Category data from API or cache
 * @returns {Object} Parsed exercise category tree
 */
export const parseExerciseCategoryTree = (categoryData) => {
  try {
    // Parse the exerciseplancategoryindex which contains the tree structure
    const categoryTree = JSON.parse(categoryData.exerciseplancategoryindex)
    return categoryTree
  } catch (error) {
    console.error('Error parsing exercise category tree:', error)
    return []
  }
}

/**
 * Get the exercise category tree (async version)
 * @returns {Promise<Object>} Parsed exercise category tree
 */
export const getExerciseCategoryTree = async () => {
  const categoryData = await loadExerciseCategoryData()
  return parseExerciseCategoryTree(categoryData)
}

/**
 * Get the exercise category tree (sync version for backward compatibility)
 * Uses cached data if available, otherwise returns empty array
 * @returns {Object} Parsed exercise category tree
 */
export const getExerciseCategoryTreeSync = () => {
  if (cachedCategoryData) {
    return parseExerciseCategoryTree(cachedCategoryData)
  }
  console.warn('⚠️ No cached category data available for sync access')
  return []
}

/**
 * Flatten the exercise category tree to get all exercises (async version)
 * @returns {Promise<Array>} Array of all exercises with their category information
 */
export const getAllExercises = async () => {
  const categoryTree = await getExerciseCategoryTree()
  const exercises = []

  const traverseTree = (nodes, parentCategory = null) => {
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        // This is a category node, traverse its children
        traverseTree(node.children, {
          name: node.name,
          isCategory: node.iscat === "1",
          parent: parentCategory
        })
      } else if (node.id && !node.exerciseplantemplate) {
        // This is an exercise node (has id but not exerciseplantemplate)
        exercises.push({
          id: node.id,
          name: node.name,
          categoryName: node.catname || (parentCategory ? parentCategory.name : 'Unknown'),
          categoryId: node.catid,
          category: parentCategory,
          sets: node.sets || '',
          reps: node.reps || '',
          rest: node.rest || '',
          speed: node.speed || '',
          mode: node.mode || '',
          intensity: node.intensity || '',
          heartrate: node.heartrate || '',
          duration: node.duration || '',
          note: node.note || '',
          isAddedManual: node.is_added_manual === 1
        })
      }
    })
  }

  traverseTree(categoryTree)
  return exercises
}

/**
 * Flatten the exercise category tree to get all exercises (sync version for backward compatibility)
 * Uses cached data if available
 * @returns {Array} Array of all exercises with their category information
 */
export const getAllExercisesSync = () => {
  const categoryTree = getExerciseCategoryTreeSync()
  const exercises = []

  const traverseTree = (nodes, parentCategory = null) => {
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        // This is a category node, traverse its children
        traverseTree(node.children, {
          name: node.name,
          isCategory: node.iscat === "1",
          parent: parentCategory
        })
      } else if (node.id && !node.exerciseplantemplate) {
        // This is an exercise node (has id but not exerciseplantemplate)
        exercises.push({
          id: node.id,
          name: node.name,
          categoryName: node.catname || (parentCategory ? parentCategory.name : 'Unknown'),
          categoryId: node.catid,
          category: parentCategory,
          sets: node.sets || '',
          reps: node.reps || '',
          rest: node.rest || '',
          speed: node.speed || '',
          mode: node.mode || '',
          intensity: node.intensity || '',
          heartrate: node.heartrate || '',
          duration: node.duration || '',
          note: node.note || '',
          isAddedManual: node.is_added_manual === 1
        })
      }
    })
  }

  traverseTree(categoryTree)
  return exercises
}

/**
 * Get exercises by category (async version)
 * @param {string} categoryName - Name of the category to filter by
 * @returns {Promise<Array>} Array of exercises in the specified category
 */
export const getExercisesByCategory = async (categoryName) => {
  const allExercises = await getAllExercises()
  return allExercises.filter(exercise => 
    exercise.categoryName.toLowerCase().includes(categoryName.toLowerCase())
  )
}

/**
 * Get exercises by category (sync version for backward compatibility)
 * @param {string} categoryName - Name of the category to filter by
 * @returns {Array} Array of exercises in the specified category
 */
export const getExercisesByCategorySync = (categoryName) => {
  const allExercises = getAllExercisesSync()
  return allExercises.filter(exercise => 
    exercise.categoryName.toLowerCase().includes(categoryName.toLowerCase())
  )
}

/**
 * Get all unique categories (async version)
 * @returns {Promise<Array>} Array of unique category objects
 */
export const getAllCategories = async () => {
  const categoryTree = await getExerciseCategoryTree()
  const categories = new Map()

  const traverseTree = (nodes, parentPath = []) => {
    nodes.forEach(node => {
      if (node.iscat === "1") {
        // This is a category
        const categoryPath = [...parentPath, node.name]
        const categoryKey = categoryPath.join(' > ')
        
        categories.set(categoryKey, {
          name: node.name,
          fullPath: categoryPath,
          displayName: categoryKey,
          level: categoryPath.length,
          parent: parentPath.length > 0 ? parentPath[parentPath.length - 1] : null
        })

        if (node.children && node.children.length > 0) {
          traverseTree(node.children, categoryPath)
        }
      }
    })
  }

  traverseTree(categoryTree)
  return Array.from(categories.values())
}

/**
 * Get all unique categories (sync version for backward compatibility)
 * @returns {Array} Array of unique category objects
 */
export const getAllCategoriesSync = () => {
  const categoryTree = getExerciseCategoryTreeSync()
  const categories = new Map()

  const traverseTree = (nodes, parentPath = []) => {
    nodes.forEach(node => {
      if (node.iscat === "1") {
        // This is a category
        const categoryPath = [...parentPath, node.name]
        const categoryKey = categoryPath.join(' > ')
        
        categories.set(categoryKey, {
          name: node.name,
          fullPath: categoryPath,
          displayName: categoryKey,
          level: categoryPath.length,
          parent: parentPath.length > 0 ? parentPath[parentPath.length - 1] : null
        })

        if (node.children && node.children.length > 0) {
          traverseTree(node.children, categoryPath)
        }
      }
    })
  }

  traverseTree(categoryTree)
  return Array.from(categories.values())
}

/**
 * Search exercises by name or category (async version)
 * @param {string} searchTerm - Search term to filter exercises
 * @returns {Promise<Array>} Array of exercises matching the search term
 */
export const searchExercises = async (searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return await getAllExercises()
  }

  const allExercises = await getAllExercises()
  const term = searchTerm.toLowerCase()

  return allExercises.filter(exercise =>
    exercise.name.toLowerCase().includes(term) ||
    exercise.categoryName.toLowerCase().includes(term) ||
    exercise.note.toLowerCase().includes(term)
  )
}

/**
 * Search exercises by name or category (sync version for backward compatibility)
 * @param {string} searchTerm - Search term to filter exercises
 * @returns {Array} Array of exercises matching the search term
 */
export const searchExercisesSync = (searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return getAllExercisesSync()
  }

  const allExercises = getAllExercisesSync()
  const term = searchTerm.toLowerCase()

  return allExercises.filter(exercise =>
    exercise.name.toLowerCase().includes(term) ||
    exercise.categoryName.toLowerCase().includes(term) ||
    exercise.note.toLowerCase().includes(term)
  )
}

/**
 * Get exercise by ID (async version)
 * @param {number|string} exerciseId - ID of the exercise to find
 * @returns {Promise<Object|null>} Exercise object or null if not found
 */
export const getExerciseById = async (exerciseId) => {
  const allExercises = await getAllExercises()
  return allExercises.find(exercise => exercise.id.toString() === exerciseId.toString()) || null
}

/**
 * Get exercise by ID (sync version for backward compatibility)
 * @param {number|string} exerciseId - ID of the exercise to find
 * @returns {Object|null} Exercise object or null if not found
 */
export const getExerciseByIdSync = (exerciseId) => {
  const allExercises = getAllExercisesSync()
  return allExercises.find(exercise => exercise.id.toString() === exerciseId.toString()) || null
}

/**
 * Get exercises grouped by main categories (async version)
 * @returns {Promise<Object>} Object with exercises grouped by main categories
 */
export const getExercisesGroupedByMainCategory = async () => {
  const allExercises = await getAllExercises()
  const grouped = {}

  allExercises.forEach(exercise => {
    // Extract main category (first part before ' - ')
    const mainCategory = exercise.categoryName.split(' - ')[0] || 'Other'
    
    if (!grouped[mainCategory]) {
      grouped[mainCategory] = []
    }
    
    grouped[mainCategory].push(exercise)
  })

  return grouped
}

/**
 * Get exercises grouped by main categories (sync version for backward compatibility)
 * @returns {Object} Object with exercises grouped by main categories
 */
export const getExercisesGroupedByMainCategorySync = () => {
  const allExercises = getAllExercisesSync()
  const grouped = {}

  allExercises.forEach(exercise => {
    // Extract main category (first part before ' - ')
    const mainCategory = exercise.categoryName.split(' - ')[0] || 'Other'
    
    if (!grouped[mainCategory]) {
      grouped[mainCategory] = []
    }
    
    grouped[mainCategory].push(exercise)
  })

  return grouped
}

/**
 * Get exercise statistics (async version)
 * @returns {Promise<Object>} Statistics about the exercise library
 */
export const getExerciseStats = async () => {
  const allExercises = await getAllExercises()
  const categories = await getAllCategories()
  const groupedByMainCategory = await getExercisesGroupedByMainCategory()

  return {
    totalExercises: allExercises.length,
    totalCategories: categories.length,
    mainCategories: Object.keys(groupedByMainCategory).length,
    exercisesByMainCategory: Object.keys(groupedByMainCategory).map(category => ({
      category,
      count: groupedByMainCategory[category].length
    })).sort((a, b) => b.count - a.count)
  }
}

/**
 * Get exercise statistics (sync version for backward compatibility)
 * @returns {Object} Statistics about the exercise library
 */
export const getExerciseStatsSync = () => {
  const allExercises = getAllExercisesSync()
  const categories = getAllCategoriesSync()
  const groupedByMainCategory = getExercisesGroupedByMainCategorySync()

  return {
    totalExercises: allExercises.length,
    totalCategories: categories.length,
    mainCategories: Object.keys(groupedByMainCategory).length,
    exercisesByMainCategory: Object.keys(groupedByMainCategory).map(category => ({
      category,
      count: groupedByMainCategory[category].length
    })).sort((a, b) => b.count - a.count)
  }
}

/**
 * Transform exercise data to match the store format
 * @param {Object} exercise - Exercise object from the category tree
 * @returns {Object} Exercise object in store format
 */
export const transformExerciseToStoreFormat = (exercise) => {
  return {
    id: exercise.id.toString(),
    name: exercise.name,
    category: exercise.categoryName.toLowerCase().replace(/[^a-z0-9]/g, ''),
    muscleGroups: [exercise.categoryName.split(' - ')[0].toLowerCase()],
    equipment: determineEquipment(exercise.categoryName),
    difficulty: 'intermediate', // Default difficulty
    tags: [exercise.categoryName.split(' - ')[0].toLowerCase()],
    image: `/exercises/${exercise.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.jpg`,
    videoUrl: `/exercises/${exercise.name.toLowerCase().replace(/[^a-z0-9]/g, '')}.mp4`,
    description: exercise.note || `${exercise.name} exercise`,
    sets: exercise.sets,
    reps: exercise.reps,
    rest: exercise.rest,
    tempo: exercise.speed,
    mode: exercise.mode,
    intensity: exercise.intensity,
    heartrate: exercise.heartrate,
    duration: exercise.duration
  }
}

/**
 * Determine equipment type from category name
 * @param {string} categoryName - Category name
 * @returns {string} Equipment type
 */
const determineEquipment = (categoryName) => {
  const category = categoryName.toLowerCase()
  
  if (category.includes('machine')) return 'machine'
  if (category.includes('cable')) return 'cable'
  if (category.includes('freeweight') || category.includes('dumbbell') || category.includes('barbell')) return 'freeweight'
  if (category.includes('bodyweight')) return 'bodyweight'
  if (category.includes('band')) return 'bands'
  if (category.includes('ball')) return 'ball'
  
  return 'other'
}

export default {
  // Async versions (primary)
  loadExerciseCategoryData,
  getExerciseCategoryTree,
  getAllExercises,
  getExercisesByCategory,
  getAllCategories,
  searchExercises,
  getExerciseById,
  getExercisesGroupedByMainCategory,
  getExerciseStats,
  transformExerciseToStoreFormat,
  
  // Sync versions (backward compatibility)
  getExerciseCategoryTreeSync,
  getAllExercisesSync,
  getExercisesByCategorySync,
  getAllCategoriesSync,
  searchExercisesSync,
  getExerciseByIdSync,
  getExercisesGroupedByMainCategorySync,
  getExerciseStatsSync
}